/**
 * useMinutesCopilotBridge — the chat↔form bridge the Minutes Studio hands to
 * its floating CopilotDock:
 *
 *   chat → form : update_page_config tool calls arrive as patch_page_config
 *                 client actions; this hook maps them onto MeetingConfigContext
 *                 (fields flash teal for ~2.4s; a toast lists the changes when
 *                 the inspector is closed).
 *   form → chat : getPageContext() snapshots the live form AND the document in
 *                 both versions — what is on screen now, plus Vee's version
 *                 from before the user hand-edited it — sent with every dock
 *                 turn (Copilot-style ambient grounding). See chat/pageDoc.js
 *                 for why the pair, not just the current text.
 *
 * Extracted from the Prototype-B shell when the bake-off converged on the
 * canvas + floating-dock hybrid.
 */
import { useRef, useState } from 'react';
import { useUI } from '../../context/UIContext';
import { useMeetingConfig } from '../../context/MeetingConfigContext';
import { buildDocPair } from '../../chat/pageDoc';

// update_page_config field → MeetingConfigContext config key.
const SCALAR_KEY_MAP = {
  meetingType: 'meetingType',
  date: 'date',
  startTime: 'startTime',
  endTime: 'endTime',
  mode: 'mode',
  venue: 'venue',
  noticeDate: 'noticeDate',
  chairperson: 'chairperson',
  companySecretary: 'companySecretary',
  statutoryAuditor: 'statutoryAuditor',
  financialYear: 'fy',
  previousMeetingDate: 'previousMeetingDate',
  previousMeetingNumber: 'previousMeetingNumber',
  proposedAgmDate: 'proposedAgmDate',
  proposedAgmVenue: 'proposedAgmVenue',
  dividendRecommendation: 'dividendRecommendation',
};

export function useMinutesCopilotBridge({ getDocState, isInspectorOpen }) {
  const { showToast } = useUI();
  const {
    config, setConfig, updateConfig,
    setAgendaChecked, setCustomItems,
    setAbsentDirectors, setInvitees,
    getPresets, selectedAgendaLabels,
  } = useMeetingConfig();

  const [flashKeys, setFlashKeys] = useState(null);
  const flashTimer = useRef(null);

  const flash = (keys) => {
    setFlashKeys(new Set(keys));
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashKeys(null), 2400);
  };

  const applyPatch = (patch) => {
    const touched = [];
    // meetingType first — changing it resets the agenda presets, so agenda
    // items in the SAME patch land after the reset.
    if (patch.meetingType && patch.meetingType !== config.meetingType) {
      updateConfig('meetingType', patch.meetingType);
      touched.push('meetingType');
    }
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'meetingType' || v === undefined) continue;
      if (k === 'meetingNumber') {
        setConfig((prev) => ({ ...prev, meetingNo: 'custom', meetingNoCustom: String(v) }));
        touched.push('meetingNumber');
      } else if (SCALAR_KEY_MAP[k]) {
        updateConfig(SCALAR_KEY_MAP[k], String(v));
        touched.push(k === 'financialYear' ? 'financialYear' : SCALAR_KEY_MAP[k]);
      } else if (k === 'agendaAdd') {
        const items = Array.isArray(v) ? v : [];
        const presets = getPresets();
        const custom = [];
        for (const item of items) {
          const preset = presets.find((p) => p.label.toLowerCase() === String(item).toLowerCase());
          if (preset) setAgendaChecked((prev) => ({ ...prev, [preset.id]: true }));
          else custom.push(String(item));
        }
        if (custom.length) {
          setCustomItems((prev) => [...prev, ...custom.filter((c) => !prev.some((p) => p.toLowerCase() === c.toLowerCase()))]);
        }
        touched.push('agenda');
      } else if (k === 'agendaRemove') {
        const items = (Array.isArray(v) ? v : []).map((s) => String(s).toLowerCase());
        for (const p of getPresets()) {
          if (items.some((t) => p.label.toLowerCase().includes(t))) {
            setAgendaChecked((prev) => ({ ...prev, [p.id]: false }));
          }
        }
        setCustomItems((prev) => prev.filter((c) => !items.some((t) => c.toLowerCase().includes(t))));
        touched.push('agenda');
      } else if (k === 'absentDirectors') {
        setAbsentDirectors((prev) => [...new Set([...prev, ...(Array.isArray(v) ? v : [])])]);
        touched.push('absentDirectors');
      } else if (k === 'invitees') {
        setInvitees((prev) => [...new Set([...prev, ...(Array.isArray(v) ? v : [])])]);
        touched.push('invitees');
      }
    }
    if (touched.length) {
      flash(touched);
      // Inspector closed → the flash is invisible; say what changed instead.
      if (!isInspectorOpen()) showToast(`Vee filled in: ${[...new Set(touched)].join(', ')}`, 'info');
    }
    return touched;
  };

  const handleClientActions = (actions) => {
    for (const a of actions || []) {
      if (a.type === 'patch_page_config' && a.target === 'minutes') applyPatch(a.patch || {});
      // navigate / switch_company directives are ignored while the studio is open.
    }
  };

  const getPageContext = () => {
    const doc = getDocState(); // { docSource, hasUserEdits, currentHtml, baselineHtml }
    return {
      page: 'minutes',
      doc: buildDocPair(doc),
      config: {
        meetingType: config.meetingType,
        meetingNumber: config.meetingNo === 'custom' ? (config.meetingNoCustom || '') : (config.meetingNo || ''),
        date: config.date,
        startTime: config.startTime,
        endTime: config.endTime,
        mode: config.mode,
        venue: config.venue,
        noticeDate: config.noticeDate,
        chairperson: config.chairperson,
        companySecretary: config.companySecretary,
        statutoryAuditor: config.statutoryAuditor,
        financialYear: config.fy,
        previousMeetingDate: config.previousMeetingDate,
        previousMeetingNumber: config.previousMeetingNumber,
        proposedAgmDate: config.proposedAgmDate,
        proposedAgmVenue: config.proposedAgmVenue,
        dividendRecommendation: config.dividendRecommendation,
      },
      agendaItems: selectedAgendaLabels,
      docSource: doc.docSource,
      hasUserEdits: doc.hasUserEdits,
    };
  };

  return { flashKeys, handleClientActions, getPageContext };
}

export default useMinutesCopilotBridge;
